import { useState } from "react";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import logo from "../assets/logo.jpg";
import ChangeLanguageComponent from "./ChangeLanguageComponent";
import { useTranslation } from "react-i18next";

export default function NavMenu() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const toggleDrawer = (open) => (event) => {
    setDrawerOpen(open);
  };

  const menuItems = [
    { id: 1, title: t("menu.homepage"), href: "/", multichildren: false },
    {
      id: 2,
      title: t("menu.about_us.title"),
      href: "",
      multichildren: true,
      children: [
        { id: 1, name: t("menu.about_us.goals"), href: "/about-us/objectives" },
        { id: 2, name: t("menu.about_us.partners"), href: "/about-us/partners" },
      ],
    },
    { id: 3, title: t("menu.activities"), href: "/activities", multichildren: false },
    { id: 4, title: t("menu.gallery"), href: "/gallery", multichildren: false },
    { id: 5, title: t("menu.news"), href: "/news", multichildren: false },
  ];

  const renderMenuItems = () => {
    return menuItems.map((item) => {
      if (!item.multichildren) {
        return (
          <ListItem
            button
            key={item.id}
            component={Link}
            to={item.href}
            onClick={toggleDrawer(false)}
          >
            <ListItemText primary={item.title} />
          </ListItem>
        );
      } else {
        if (isMobile) {
          // Render children directly as menu items for mobile view
          return item.children.map((child) => (
            <ListItem
              button
              key={child.id}
              component={Link}
              to={child.href}
              onClick={toggleDrawer(false)}
            >
              <ListItemText primary={child.name} />
            </ListItem>
          ));
        } else {
          // Render menu with submenu for desktop view
          return (
            <>
              <ListItem button key={item.id} onClick={handleClick}>
                <ListItemText primary={item.title} />
              </ListItem>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {item.children.map((child) => (
                  <MenuItem key={child.id} component={Link} to={child.href} onClick={handleClose}>
                    {child.name}
                  </MenuItem>
                ))}
              </Menu>
            </>
          );
        }
      }
    });
  };

  return (
    <>
      <AppBar position={"static"} sx={{ backgroundColor: "#fff", paddingY: 2 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <img src={logo} alt="Logo" style={{ width: 126 }} />
          </Box>
          {isMobile ? (
            <>
              <IconButton
                sx={{ color: "#432775" }}
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
              >
                <MenuIcon />
              </IconButton>
              <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                <List onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
                  {renderMenuItems()}
                  <ListItem>
                    <ChangeLanguageComponent />
                  </ListItem>
                </List>
              </Drawer>
            </>
          ) : (
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexGrow: 1 }}
            >
              {menuItems.map((item) => {
                if (!item.multichildren) {
                  return (
                    <Typography
                      key={item.id}
                      sx={{
                        color: "#444",
                        marginRight: "30px",
                        fontWeight: "700",
                        fontSize: "14px",
                        letterSpacing: "1px",
                      }}
                    >
                      <Link to={item.href} style={{ textDecoration: "none", color: "inherit" }}>
                        {item.title}
                      </Link>
                    </Typography>
                  );
                } else {
                  return (
                    <>
                      <IconButton
                        onMouseEnter={handleClick}
                        size="small"
                        aria-controls={open ? "account-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                      >
                        <Typography
                          sx={{
                            color: "#444",
                            marginRight: "30px",
                            fontWeight: "700",
                            fontSize: "14px",
                            letterSpacing: "1px",
                          }}
                        >
                          {item.title}
                        </Typography>
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                          onMouseLeave: handleClose,
                        }}
                        PaperProps={{
                          elevation: 0,
                          sx: {
                            borderTop: "3px solid #42286d",
                            overflow: "visible",
                            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                            mt: 1.5,
                            "&::before": {
                              content: '""',
                              display: "block",
                              position: "absolute",
                              top: 0,
                              right: 14,
                              width: 10,
                              height: 10,
                              bgcolor: "#fff",
                              transform: "translateY(-50%) rotate(45deg)",
                              zIndex: 0,
                            },
                          },
                        }}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                      >
                        {item.children.map((child) => (
                          <Link
                            to={child.href}
                            style={{ textDecoration: "none", color: "inherit" }}
                            key={child.id}
                          >
                            <MenuItem onClick={handleClose}>
                              <Typography>{child.name}</Typography>
                            </MenuItem>
                          </Link>
                        ))}
                      </Menu>
                    </>
                  );
                }
              })}
              <Box sx={{ marginLeft: "auto" }}>
                <ChangeLanguageComponent />
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}
